import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { 
  Search, ArrowRight, Building2, Calendar, 
  FileText, User, Loader2
} from 'lucide-react';
import { getAssetIcon } from '../../utils/industryIcons';

const MAX_RESULTS_PER_CATEGORY = 3;
const EMPTY_RESULTS = { jobs: [], assets: [], operators: [], customers: [], dockets: [] };

const GlobalSearch = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef(null);
  
  const AssetIcon = getAssetIcon(user?.industry);

  // Perform search - called directly, not in useEffect
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2 || !api) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    try {
      const [jobsRes, equipmentRes, operatorsRes, customersRes, docketsRes] = await Promise.all([
        api.get('/jobs').catch(() => ({ data: [] })),
        api.get('/equipment').catch(() => ({ data: [] })),
        api.get('/operators').catch(() => ({ data: [] })),
        api.get('/customers').catch(() => ({ data: [] })),
        api.get('/dockets').catch(() => ({ data: [] })),
      ]);

      const q = searchQuery.toLowerCase();
      const filter = (items, fields) => {
        if (!Array.isArray(items)) return [];
        return items.filter(item => 
          fields.some(f => item[f] && String(item[f]).toLowerCase().includes(q))
        ).slice(0, 10);
      };

      const newResults = {
        jobs: filter(jobsRes.data || [], ['job_number', 'description', 'po_number']),
        assets: filter(equipmentRes.data || [], ['asset_id', 'name', 'equipment_type']),
        operators: filter(operatorsRes.data || [], ['name', 'email', 'phone']),
        customers: filter(customersRes.data || [], ['company_name', 'contact_name', 'email']),
        dockets: filter(docketsRes.data || [], ['docket_number', 'job_number']),
      };
      
      setResults(newResults);
      setLoading(false);
    } catch (err) {
      console.error('Search error:', err);
      setResults(EMPTY_RESULTS);
      setLoading(false);
    }
  }, [api]);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
    
    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (value.length < 2) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    // Debounce the search
    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Handle click outside
  const handleBlur = (e) => {
    // Delay to allow click on results
    setTimeout(() => {
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 200);
  };

  // Handle keyboard shortcut
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Global keyboard shortcut
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    }, { once: false });
  }

  const handleSelect = (type, item) => {
    setIsOpen(false);
    setQuery('');
    setResults(EMPTY_RESULTS);
    const routes = {
      job: `/schedule?job=${item.id}`,
      asset: `/equipment?id=${item.id}`,
      operator: `/operators?id=${item.id}`,
      customer: `/customers?id=${item.id}`,
      docket: `/dockets?id=${item.id}`,
    };
    navigate(routes[type] || '/');
  };

  const handleViewAll = (type) => {
    setIsOpen(false);
    const routes = {
      jobs: `/schedule?search=${encodeURIComponent(query)}`,
      assets: `/equipment?search=${encodeURIComponent(query)}`,
      operators: `/operators?search=${encodeURIComponent(query)}`,
      customers: `/customers?search=${encodeURIComponent(query)}`,
      dockets: `/dockets?search=${encodeURIComponent(query)}`,
    };
    navigate(routes[type] || '/');
  };

  const hasResults = Object.values(results).some(arr => arr.length > 0);
  const showDropdown = isOpen && (query.length >= 2 || loading);

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-500/20 text-green-400 border-green-500/30',
      'available': 'bg-green-500/20 text-green-400 border-green-500/30',
      'allocated': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'confirmed': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'not_allocated': 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
      'docketed': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'invoiced': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input 
          ref={inputRef}
          type="text"
          placeholder="Search EQUIPR..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-9 pl-9 pr-12 text-sm bg-zinc-800/50 border border-zinc-700 rounded-lg outline-none placeholder:text-zinc-500 text-white focus:border-zinc-600 focus:bg-zinc-800"
        />
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 select-none items-center gap-0.5 rounded border border-zinc-700 bg-zinc-800 px-1.5 font-mono text-[10px] text-zinc-400">
          <span>⌘</span>K
        </kbd>
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          )}

          {/* No Results */}
          {!loading && query.length >= 2 && !hasResults && (
            <div className="py-8 text-center text-sm text-zinc-500">
              No results found for "{query}"
            </div>
          )}

          {/* Results */}
          {!loading && hasResults && (
            <div className="divide-y divide-zinc-800/50">
              {/* Assets */}
              {results.assets.length > 0 && (
                <div className="py-2">
                  <div className="flex items-center justify-between px-3 py-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Assets</span>
                    {results.assets.length > MAX_RESULTS_PER_CATEGORY && (
                      <button onClick={() => handleViewAll('assets')} className="text-[10px] text-[#ffd108] hover:text-[#ffe54c] flex items-center gap-1">
                        View all <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {results.assets.slice(0, MAX_RESULTS_PER_CATEGORY).map((asset) => (
                    <div 
                      key={asset.id}
                      onClick={() => handleSelect('asset', asset)}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400">
                        <AssetIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">{asset.name || asset.asset_id}</div>
                        <div className="text-xs text-zinc-500 truncate">{asset.asset_id} • {asset.equipment_type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Jobs */}
              {results.jobs.length > 0 && (
                <div className="py-2">
                  <div className="flex items-center justify-between px-3 py-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Jobs</span>
                    {results.jobs.length > MAX_RESULTS_PER_CATEGORY && (
                      <button onClick={() => handleViewAll('jobs')} className="text-[10px] text-[#ffd108] hover:text-[#ffe54c] flex items-center gap-1">
                        View all <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {results.jobs.slice(0, MAX_RESULTS_PER_CATEGORY).map((job) => (
                    <div 
                      key={job.id}
                      onClick={() => handleSelect('job', job)}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400">
                        <Calendar className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">{job.job_number}</div>
                        <div className="text-xs text-zinc-500 truncate">{job.description || job.equipment_type} • {job.start_date}</div>
                      </div>
                      {job.status && (
                        <Badge variant="outline" className={`text-[10px] ${getStatusColor(job.status)}`}>
                          {job.status?.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Operators */}
              {results.operators.length > 0 && (
                <div className="py-2">
                  <div className="flex items-center justify-between px-3 py-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Operators</span>
                    {results.operators.length > MAX_RESULTS_PER_CATEGORY && (
                      <button onClick={() => handleViewAll('operators')} className="text-[10px] text-[#ffd108] hover:text-[#ffe54c] flex items-center gap-1">
                        View all <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {results.operators.slice(0, MAX_RESULTS_PER_CATEGORY).map((operator) => (
                    <div 
                      key={operator.id}
                      onClick={() => handleSelect('operator', operator)}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-500/20 text-green-400">
                        <User className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">{operator.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{operator.email || operator.phone || 'Operator'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Customers */}
              {results.customers.length > 0 && (
                <div className="py-2">
                  <div className="flex items-center justify-between px-3 py-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Customers</span>
                    {results.customers.length > MAX_RESULTS_PER_CATEGORY && (
                      <button onClick={() => handleViewAll('customers')} className="text-[10px] text-[#ffd108] hover:text-[#ffe54c] flex items-center gap-1">
                        View all <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {results.customers.slice(0, MAX_RESULTS_PER_CATEGORY).map((customer) => (
                    <div 
                      key={customer.id}
                      onClick={() => handleSelect('customer', customer)}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-500/20 text-purple-400">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">{customer.company_name}</div>
                        <div className="text-xs text-zinc-500 truncate">{customer.contact_name} • {customer.phone || customer.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Dockets */}
              {results.dockets.length > 0 && (
                <div className="py-2">
                  <div className="flex items-center justify-between px-3 py-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Dockets</span>
                    {results.dockets.length > MAX_RESULTS_PER_CATEGORY && (
                      <button onClick={() => handleViewAll('dockets')} className="text-[10px] text-[#ffd108] hover:text-[#ffe54c] flex items-center gap-1">
                        View all <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {results.dockets.slice(0, MAX_RESULTS_PER_CATEGORY).map((docket) => (
                    <div 
                      key={docket.id}
                      onClick={() => handleSelect('docket', docket)}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">{docket.docket_number}</div>
                        <div className="text-xs text-zinc-500 truncate">Job: {docket.job_number} • {docket.created_at?.split('T')[0]}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
